import type { BuildState, SlotKey } from './build-state.svelte.js';
import { isOverrideKeyAllowed, clampOverride } from './build-state.svelte.js';
import type { ModSlot } from './data/weapon-mods.js';

const SLOT_KEYS: SlotKey[] = ['chest', 'backpack', 'gloves', 'mask', 'holster', 'kneepads'];
const MOD_SLOTS: ModSlot[] = ['optic', 'muzzle', 'underbarrel', 'magazine'];

export function encodeBuild(b: BuildState): string {
  const params = new URLSearchParams();
  if (b.weaponId) params.set('w', b.weaponId);
  if (b.weaponTalentActive) params.set('wt', '1');
  if (b.customWeaponTalentId) params.set('wtal', b.customWeaponTalentId);
  if (b.weaponAttrSelected) params.set('wa', b.weaponAttrSelected);
  for (const k of SLOT_KEYS) {
    const slot = b.gear[k];
    // Format: <brand>,<set>,<core>,<attr1>,<attr2>,<mod>,<named>,<isProto>,<protoAugment>
    // isProto/protoAugment appended at end for backwards compat — older URLs without
    // these fields decode as isPrototype=false / prototypeAugmentId=null.
    const parts = [
      slot.brandId ?? '',
      slot.setId ?? '',
      slot.coreStat,
      slot.attr1 ?? '',
      slot.attr2 ?? '',
      slot.modAttr ?? '',
      slot.namedId ?? '',
      slot.isPrototype ? '1' : '',
      slot.prototypeAugmentId ?? '',
    ];
    if (parts.some((p) => p)) params.set(k, parts.join(','));
  }
  if (b.chestTalentId) {
    params.set('ct', b.chestTalentId + (b.chestTalentActive ? ',1' : ',0'));
  }
  if (b.backpackTalentId) {
    params.set('bt', b.backpackTalentId + (b.backpackTalentActive ? ',1' : ',0'));
  }
  if (b.shdWatchActive) params.set('shd', '1');
  if (b.specializationId) params.set('spec', b.specializationId);
  if (b.specClassPicks && b.specClassPicks.length > 0) {
    params.set('scp', b.specClassPicks.join(','));
  }
  if (b.expertiseGrade > 0) params.set('exp', String(b.expertiseGrade));
  if (b.headshotChancePct > 0) params.set('hs', String(b.headshotChancePct));
  if (!b.fullArmor) params.set('fa', '0'); // default true, only encode if false
  if (b.targetStatus !== 'none') params.set('ts', b.targetStatus);
  if (b.targetPulsed) params.set('tp', '1');
  if (b.targetMarked) params.set('tm', '1');
  if (b.targetBleeding) params.set('tb', '1');
  if (b.firstShotMode) params.set('fs', '1');
  if (b.harrierPerfectCount !== 3) params.set('hp', String(b.harrierPerfectCount));
  if (!b.ravenousActive) params.set('rv', '0');
  if (b.groupSize && b.groupSize !== 1) params.set('gs', String(b.groupSize));
  // Set stacks
  const stackKeys = Object.keys(b.setStacks ?? {});
  if (stackKeys.length) {
    const parts = stackKeys.map((k) => `${k}:${b.setStacks[k]}`).join(';');
    if (parts) params.set('ss', parts);
  }
  const chestTal = Object.entries(b.setChestTalent ?? {}).filter(([, v]) => v).map(([k]) => k).join(';');
  if (chestTal) params.set('sct', chestTal);
  const bpTal = Object.entries(b.setBpTalent ?? {}).filter(([, v]) => v).map(([k]) => k).join(';');
  if (bpTal) params.set('sbt', bpTal);
  for (const ms of MOD_SLOTS) {
    const mid = b.weaponMods[ms];
    if (mid) params.set('m_' + ms, mid);
  }
  // PRO-mode manual overrides — encode as "stat:val;stat:val".
  if (b.manualOverrides) {
    const ovrParts = Object.entries(b.manualOverrides)
      .filter(([, v]) => Number.isFinite(v))
      .map(([k, v]) => `${k}:${v}`);
    if (ovrParts.length) params.set('mo', ovrParts.join(';'));
  }
  // Verify values (PRO mode) — in-game numbers user typed for calc-vs-game comparison.
  // Same format/whitelist as `mo`. Not applied to formula, pure UI overlay.
  if (b.verifyValues) {
    const vfParts = Object.entries(b.verifyValues)
      .filter(([, v]) => Number.isFinite(v))
      .map(([k, v]) => `${k}:${v}`);
    if (vfParts.length) params.set('vf', vfParts.join(';'));
  }
  // Recombinator: encode 3 slots as "stat:value;stat:value;stat:value"
  // Empty slots encoded as ":0" so positions are preserved.
  if (b.recombinator?.length) {
    const recParts = b.recombinator.map((r) =>
      r?.stat ? `${r.stat}:${r.value}` : ''
    );
    if (recParts.some((p) => p)) {
      params.set('rec', recParts.join(';'));
    }
  }
  return params.toString();
}

export function applyUrlToBuild(b: BuildState, params: URLSearchParams): void {
  const w = params.get('w');
  if (w) b.weaponId = w;
  if (params.get('wt') === '1') b.weaponTalentActive = true;
  const wtal = params.get('wtal');
  if (wtal) b.customWeaponTalentId = wtal;
  const wa = params.get('wa');
  if (wa) b.weaponAttrSelected = wa;

  for (const k of SLOT_KEYS) {
    const raw = params.get(k);
    if (!raw) continue;
    const [brand, set, core, a1, a2, mod, named, isProto, protoAugment] = raw.split(',');
    // Named gear takes priority — when set, brand/set are mutually exclusive (handled by setSlotNamed)
    if (named) {
      b.setSlotNamed(k, named);
    } else {
      if (brand) b.setSlotBrand(k, brand);
      if (set) b.setSlotSet(k, set);
    }
    if (core) b.setSlotCore(k, core as 'wd' | 'armor' | 'skill_tier');
    if (a1) b.setSlotAttr(k, 'attr1', a1 as never);
    if (a2) b.setSlotAttr(k, 'attr2', a2 as never);
    if (mod) b.setSlotAttr(k, 'modAttr', mod as never);
    // Prototype fields — backwards compatible: missing/empty → false/null.
    if (isProto === '1') b.setSlotPrototype(k, true);
    if (protoAugment) b.setSlotPrototypeAugment(k, protoAugment);
  }

  const ct = params.get('ct');
  if (ct) {
    const [id, active] = ct.split(',');
    b.chestTalentId = id ?? null;
    b.chestTalentActive = active === '1';
  }
  const bt = params.get('bt');
  if (bt) {
    const [id, active] = bt.split(',');
    b.backpackTalentId = id ?? null;
    b.backpackTalentActive = active === '1';
  }
  if (params.get('shd') === '1') b.shdWatchActive = true;
  const spec = params.get('spec');
  if (spec) b.specializationId = spec;
  const scp = params.get('scp');
  if (scp) b.setSpecClassPicks(scp.split(',').map((s) => s.trim()).filter(Boolean));
  const exp = params.get('exp');
  if (exp) b.expertiseGrade = parseInt(exp, 10) || 0;
  b.headshotChancePct = parseInt(params.get('hs') ?? '0', 10) || 0;
  if (params.get('fa') === '0') b.fullArmor = false;
  const ts = params.get('ts');
  if (ts) b.targetStatus = ts as typeof b.targetStatus;
  if (params.get('tp') === '1') b.targetPulsed = true;
  if (params.get('tm') === '1') b.targetMarked = true;
  if (params.get('tb') === '1') b.targetBleeding = true;
  if (params.get('fs') === '1') b.firstShotMode = true;
  const hp = params.get('hp');
  if (hp != null) b.harrierPerfectCount = parseInt(hp, 10) || 0;
  if (params.get('rv') === '0') b.ravenousActive = false;
  const gs = params.get('gs');
  if (gs) b.groupSize = (parseInt(gs, 10) || 1) as typeof b.groupSize;
  const ss = params.get('ss');
  if (ss) {
    for (const part of ss.split(';')) {
      const [k, v] = part.split(':');
      if (k && v) b.setSetStacks(k, parseInt(v, 10) || 0);
    }
  }
  const sct = params.get('sct');
  if (sct) {
    for (const id of sct.split(';')) if (id) b.setSetChestTalent(id, true);
  }
  const sbt = params.get('sbt');
  if (sbt) {
    for (const id of sbt.split(';')) if (id) b.setSetBpTalent(id, true);
  }
  for (const ms of MOD_SLOTS) {
    const mid = params.get('m_' + ms);
    if (mid) b.setWeaponMod(ms, mid);
  }
  // PRO-mode manual overrides
  const mo = params.get('mo');
  if (mo) {
    for (const part of mo.split(';')) {
      const [stat, valStr] = part.split(':');
      if (!stat || valStr === undefined) continue;
      const val = parseFloat(valStr);
      if (!Number.isFinite(val)) continue;
      if (!isOverrideKeyAllowed(stat)) continue;
      b.setManualOverride(stat, clampOverride(stat, val));
    }
  }
  // Verify values (PRO mode) — in-game numbers, same whitelist as `mo`, not applied to formula.
  const vf = params.get('vf');
  if (vf) {
    for (const part of vf.split(';')) {
      const [stat, valStr] = part.split(':');
      if (!stat || valStr === undefined) continue;
      const val = parseFloat(valStr);
      if (!Number.isFinite(val)) continue;
      if (!isOverrideKeyAllowed(stat)) continue;
      b.setVerifyValue(stat, val);
    }
  }
  // Recombinator: 3 slots as "stat:value;stat:value;stat:value"
  const rec = params.get('rec');
  if (rec) {
    const slots = rec.split(';');
    for (let i = 0; i < Math.min(3, slots.length); i++) {
      const [stat, valStr] = slots[i].split(':');
      const val = parseInt(valStr ?? '0', 10) || 0;
      if (stat && val > 0) {
        b.setRecombinatorSlot(i, stat as never, val);
      }
    }
  }
}
